/**
 * useCentralBillingLinks — who may manage billing, and where Central's billing
 * pages are for this workspace (suite contract 1.2).
 *
 *   owners / admins (manageBilling)  → a link to Central's plans / billing page
 *   everyone else                    → "Ask your workspace owner" — never a
 *                                      button into a page they cannot use
 *
 * FAIL CLOSED: until boot has completed, or when Central stated no permission,
 * the user is treated as NOT able to manage billing.
 *
 * Safe outside a Redux <Provider> (some surfaces — e.g. the upgrade modal — are
 * rendered standalone in tests and previews): with no store it answers "cannot
 * manage" and no workspace, rather than throwing.
 */

import { createContext, useCallback, useContext, useSyncExternalStore, type Context } from 'react';
import * as ReactRedux from 'react-redux';
import { hasWorkspacePermission } from '@levelup/central-context';
import type { CentralContext } from '@levelup/central-context';
import { parseJwtWorkspaceId } from '@levelup/central-auth';
import { APP_ID, CENTRAL_URL } from '../config/centralAuth';
import { appReturnUrl, centralBillingUrl, type CentralBillingView } from '../services/centralBillingUrls';

interface BillingRelevantState {
  bootContext?: {
    bootStatus?: string;
    currentWorkspace?: {
      id?: string;
      role?: string;
      scopes?: string[];
      capabilities?: unknown;
      permissions?: Record<string, boolean>;
    } | null;
    meta?: unknown;
  };
  centralAuth?: { accessToken?: string | null };
}

/** manageBilling exactly as useCentralConsumer().hasWorkspacePermission answers it. */
export function selectCanManageBilling(state: unknown): boolean {
  const s = state as BillingRelevantState | null | undefined;
  const boot = s?.bootContext;
  if (!boot || boot.bootStatus !== 'ready' || !boot.currentWorkspace) return false;
  const ws = boot.currentWorkspace;
  const context = {
    currentWorkspace: {
      role: ws.role,
      scopes: ws.scopes,
      capabilities: ws.capabilities,
      permissions: ws.permissions,
    },
    apps: [],
    meta: boot.meta ?? undefined,
  } as unknown as CentralContext;
  try {
    return hasWorkspacePermission(context, 'manageBilling');
  } catch {
    return false;
  }
}

/** The workspace Central's billing pages are addressed by. */
export function selectBillingWorkspaceId(state: unknown): string | null {
  const s = state as BillingRelevantState | null | undefined;
  const fromBoot = s?.bootContext?.currentWorkspace?.id;
  if (fromBoot) return fromBoot;
  try {
    return parseJwtWorkspaceId(s?.centralAuth?.accessToken ?? null);
  } catch {
    return null;
  }
}

/** True once Central's switch-context has been applied (permissions are known). */
export function selectBillingBootReady(state: unknown): boolean {
  return (state as BillingRelevantState | null | undefined)?.bootContext?.bootStatus === 'ready';
}

interface Snapshot {
  canManage: boolean;
  workspaceId: string | null;
  bootReady: boolean;
}

const NO_STORE: Snapshot = { canManage: false, workspaceId: null, bootReady: false };
const noopSubscribe = () => () => undefined;

export interface CentralBillingLinks extends Snapshot {
  /** Central's page for `view`, returning to `returnPath` (default: this page). Null when unaddressable. */
  urlFor: (view: CentralBillingView, returnPath?: string) => string | null;
}

type StoreLike = { getState: () => unknown; subscribe: (cb: () => void) => () => void };

/**
 * react-redux's context, resolved once. A module mock of react-redux that does
 * not export it (several page tests stub only useSelector/useDispatch) must
 * degrade to "no store", not throw from every component that renders a billing
 * link.
 */
export const ReduxContext: Context<{ store: StoreLike } | null> = (() => {
  try {
    const ctx = (ReactRedux as unknown as { ReactReduxContext?: unknown }).ReactReduxContext;
    if (ctx) return ctx as Context<{ store: StoreLike } | null>;
  } catch {
    /* mocked module without the export */
  }
  return createContext<{ store: StoreLike } | null>(null);
})();

export function useCentralBillingLinks(): CentralBillingLinks {
  const ctx = useContext(ReduxContext);
  const store = ctx?.store ?? null;

  const subscribe = useCallback(
    (cb: () => void) => (store ? store.subscribe(cb) : noopSubscribe()),
    [store],
  );

  // Primitive snapshots (stable under useSyncExternalStore's equality). The
  // "server" snapshot reads the same store: this SPA never hydrates, and a
  // store-less fallback there would render every owner as a member.
  const getCanManage = () => (store ? selectCanManageBilling(store.getState()) : NO_STORE.canManage);
  const getWorkspaceId = () => (store ? selectBillingWorkspaceId(store.getState()) : NO_STORE.workspaceId);
  const getBootReady = () => (store ? selectBillingBootReady(store.getState()) : NO_STORE.bootReady);
  const canManage = useSyncExternalStore(subscribe, getCanManage, getCanManage);
  const workspaceId = useSyncExternalStore(subscribe, getWorkspaceId, getWorkspaceId);
  const bootReady = useSyncExternalStore(subscribe, getBootReady, getBootReady);

  const urlFor = useCallback(
    (view: CentralBillingView, returnPath?: string) =>
      centralBillingUrl({
        centralUrl: CENTRAL_URL,
        workspaceId,
        view,
        appId: APP_ID,
        returnTo: appReturnUrl(returnPath),
      }),
    [workspaceId],
  );

  return { canManage, workspaceId, bootReady, urlFor };
}
